import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Send, MessageSquare, Trash2, Lock, Coins, Unlock, Smile, Medal, Users, Circle, Ban, UserCheck, X } from 'lucide-react';
import api from '../lib/api';
import { io } from 'socket.io-client';
import { usePresence } from '../contexts/PresenceContext';

const UNLOCK_COST = 100000;

// Emoji list
const EMOJI_LIST = [
  '😀', '😂', '😍', '🥰', '😎', '🤩', '😜', '🤪', '😏', '🥺',
  '😢', '😭', '😤', '😡', '🤯', '😱', '🤔', '🙄', '😴', '🤮',
  '👍', '👎', '👏', '🙌', '🤝', '✌️', '🤞', '💪', '🎮', '🏆',
  '⚽', '🥅', '🔥', '💯', '⭐', '🎯', '🚀', '💥', '❤️', '💔',
  '😈', '👹', '💀', '👻', '🎉', '🎊', '🏅', '🥇', '🥈', '🥉'
];

const Chat = () => {
  const { user, refreshUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [chatStatus, setChatStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [connected, setConnected] = useState(false);
  const bottomRef = useRef(null);
  const emojiRef = useRef(null);
  const userMenuRef = useRef(null);
  const socketRef = useRef(null);

  const [showMobileUsers, setShowMobileUsers] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // { user_id: gamertag }
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const otherTypingTimeoutsRef = useRef({});
  const localTypingActiveRef = useRef(false);
  const { emitTyping, onTyping } = usePresence();

  const isSuperAdmin = user?.role === 'super_admin';
  const isModerator = user?.role === 'moderator' || user?.role === 'admin' || isSuperAdmin;

  // Close emoji picker and user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiRef.current && !emojiRef.current.contains(event.target)) {
        setShowEmoji(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setSelectedUser(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add emoji to message
  const addEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
  };

  // Check chat status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await api.get('/chat/status');
        setChatStatus(res.data);
      } catch (error) {
        console.error('Error checking chat status:', error);
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, []);

  // Fetch messages (initial load only)
  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get('/chat/messages', { params: { limit: 50 } });
      if (res.data && Array.isArray(res.data)) {
        setMessages(res.data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    }
  }, []);

  // Socket.IO connection
  useEffect(() => {
    if (!user) return;
    let mounted = true;

    // Load message history first
    fetchMessages();

    // Fetch all online users (from heartbeat presence)
    const fetchOnlineUsers = async () => {
      try {
        const res = await api.get('/chat/online-users');
        if (mounted && res.data && Array.isArray(res.data)) {
          setOnlineUsers(res.data);
        }
      } catch (e) { /* silent */ }
    };
    fetchOnlineUsers();
    const onlineInterval = setInterval(fetchOnlineUsers, 30000);

    const BACKEND_URL = window.location.origin;
    const socket = io(BACKEND_URL, {
      path: '/api/sio/',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      if (!mounted) return;
      setConnected(true);
      socket.emit('register_user', {
        user_id: user.user_id,
        gamertag: user.gamertag,
        name: user.name,
        role: user.role,
      });
    });

    socket.on('disconnect', () => {
      if (!mounted) return;
      setConnected(false);
    });

    socket.on('new_message', (msg) => {
      if (!mounted) return;
      setMessages(prev => {
        if (prev.some(m => m.message_id === msg.message_id)) return prev;
        return [...prev, msg];
      });
    });

    socket.on('chat_cleared', () => {
      if (!mounted) return;
      setMessages([]);
    });

    socket.on('online_users', (socketUsers) => {
      if (!mounted) return;
      // Merge socket users into the existing heartbeat-based list
      setOnlineUsers(prev => {
        const merged = [...prev];
        for (const su of socketUsers) {
          if (!merged.some(u => u.user_id === su.user_id)) {
            merged.push(su);
          }
        }
        return merged;
      });
    });

    socket.on('user_joined', (userInfo) => {
      if (!mounted) return;
      setOnlineUsers(prev => {
        if (prev.some(u => u.user_id === userInfo.user_id)) return prev;
        return [...prev, userInfo];
      });
    });

    socket.on('user_left', () => {
      // Don't remove users on socket leave - the API polling handles presence
      // This prevents heartbeat users from disappearing when socket disconnects
    });

    return () => {
      mounted = false;
      clearInterval(onlineInterval);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user, fetchMessages]);

  // Fetch banned users (for moderators)
  const fetchBannedUsers = useCallback(async () => {
    if (!isModerator) return;
    try {
      const res = await api.get('/moderation/banned-users');
      if (res.data && Array.isArray(res.data)) {
        setBannedUsers(res.data.map(u => u.user_id));
      }
    } catch (error) {
      console.error('Error fetching banned users:', error);
    }
  }, [isModerator]);

  useEffect(() => {
    if (isModerator) {
      fetchBannedUsers();
    }
  }, [isModerator, fetchBannedUsers]);

  // Listen for typing events on the global chat room
  useEffect(() => {
    if (!user) return;
    const room = 'chat_broadcast';
    const off = onTyping((payload) => {
      if (payload?.room !== room) return;
      if (payload?.user_id === user.user_id) return;
      const uid = payload.user_id;
      if (!uid) return;
      if (payload?.state === 'start') {
        setTypingUsers(prev => ({ ...prev, [uid]: payload.gamertag || '?' }));
        if (otherTypingTimeoutsRef.current[uid]) clearTimeout(otherTypingTimeoutsRef.current[uid]);
        otherTypingTimeoutsRef.current[uid] = setTimeout(() => {
          setTypingUsers(prev => {
            const next = { ...prev };
            delete next[uid];
            return next;
          });
          delete otherTypingTimeoutsRef.current[uid];
        }, 4000);
      } else {
        setTypingUsers(prev => {
          const next = { ...prev };
          delete next[uid];
          return next;
        });
        if (otherTypingTimeoutsRef.current[uid]) {
          clearTimeout(otherTypingTimeoutsRef.current[uid]);
          delete otherTypingTimeoutsRef.current[uid];
        }
      }
    });
    return () => {
      off();
      Object.values(otherTypingTimeoutsRef.current).forEach(clearTimeout);
      otherTypingTimeoutsRef.current = {};
      setTypingUsers({});
    };
  }, [user, onTyping]);

  // Ban user
  const handleBanUser = async (userId, gamertag) => {
    try {
      await api.post(`/moderation/ban-user/${userId}`);
      toast.success(`${gamertag} è stato bannato dalla chat`);
      setBannedUsers(prev => [...prev, userId]);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel bannare l\'utente');
    }
  };

  // Unban user
  const handleUnbanUser = async (userId, gamertag) => {
    try {
      await api.post(`/moderation/unban-user/${userId}`);
      toast.success(`${gamertag} è stato sbannato`);
      setBannedUsers(prev => prev.filter(id => id !== userId));
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nello sbannare l\'utente');
    }
  };

  // Scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Unlock chat
  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      const res = await api.post('/chat/unlock');
      toast.success(res.data.message);
      setChatStatus(prev => ({ ...prev, chat_unlocked: true }));
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante lo sblocco');
    }
    setUnlocking(false);
  };

  // Send message
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setShowMentions(false);
    setSending(true);
    try {
      const res = await api.post('/chat/messages', { content: newMessage.trim() });
      setNewMessage('');
      // Optimistic append: show the message immediately without waiting for the socket echo
      if (res.data?.message_id) {
        setMessages(prev => {
          if (prev.some(m => m.message_id === res.data.message_id)) return prev;
          return [...prev, res.data];
        });
      }
      // Stop typing indicator
      if (localTypingActiveRef.current) {
        localTypingActiveRef.current = false;
        emitTyping('chat_broadcast', 'stop');
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio');
    }
    setSending(false);
  };

  // Handle input change with @mention detection
  const handleInputChange = (e) => {
    const val = e.target.value;
    setNewMessage(val);

    // Typing indicator throttle
    if (val.length > 0) {
      if (!localTypingActiveRef.current) {
        localTypingActiveRef.current = true;
        emitTyping('chat_broadcast', 'start');
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        localTypingActiveRef.current = false;
        emitTyping('chat_broadcast', 'stop');
      }, 2500);
    } else {
      if (localTypingActiveRef.current) {
        localTypingActiveRef.current = false;
        emitTyping('chat_broadcast', 'stop');
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    }

    // Detect @mention
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\S*)$/);
    if (mentionMatch) {
      const query = mentionMatch[1];
      setMentionQuery(query);
      if (query.length >= 1) {
        api.get('/chat/search-users', { params: { q: query } })
          .then(res => { setMentionResults(res.data || []); setShowMentions(true); })
          .catch(() => setShowMentions(false));
      } else {
        // Show online users as suggestions when just "@" is typed
        setMentionResults(onlineUsers.filter(u => u.user_id !== user?.user_id).slice(0, 8));
        setShowMentions(true);
      }
    } else {
      setShowMentions(false);
    }
  };

  // Insert mention into message
  const insertMention = (gamertag) => {
    const cursorPos = inputRef.current?.selectionStart || newMessage.length;
    const textBeforeCursor = newMessage.slice(0, cursorPos);
    const textAfterCursor = newMessage.slice(cursorPos);
    const beforeMention = textBeforeCursor.replace(/@\S*$/, '');
    setNewMessage(beforeMention + '@' + gamertag + ' ' + textAfterCursor);
    setShowMentions(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Clear chat
  const handleClearChat = async () => {
    const confirmed = window.confirm('Sei sicuro di voler svuotare tutta la chat?');
    if (!confirmed) {
      console.log('Clear chat cancelled');
      return;
    }
    
    console.log('Clearing chat...');
    setClearing(true);
    try {
      const res = await api.delete('/chat/messages');
      console.log('Clear response:', res.data);
      toast.success(res.data.message || 'Chat svuotata');
      setMessages([]);
    } catch (error) {
      console.error('Clear error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante lo svuotamento');
    }
    setClearing(false);
  };

  // Format time
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('it-IT', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Get role info
  const getRoleInfo = (role) => {
    switch (role) {
      case 'super_admin': return { label: 'Super Admin', color: 'text-yellow-400' };
      case 'admin': return { label: 'Admin', color: 'text-purple-400' };
      case 'captain': return { label: 'Capitano', color: 'text-blue-400' };
      default: return { label: 'Player', color: 'text-slate-400' };
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Render message content with highlighted @mentions
  const renderContent = (content) => {
    const parts = content.split(/(@\S+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return <span key={i} className="text-[#00F0FF] font-bold cursor-pointer hover:underline">{part}</span>;
      }
      return part;
    });
  };

  // Chat view
  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex gap-4">
        {/* Sidebar - Online Users */}
        <div className="w-64 flex-shrink-0 hidden md:block">
          <div className="bg-[#1a1f2e] rounded-xl p-4 sticky top-20">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-green-400" />
              <h2 className="font-semibold text-white">Utenti Online</h2>
              <span className="ml-auto bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">
                {onlineUsers.length}
              </span>
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto" ref={userMenuRef}>
              {onlineUsers.map((u) => {
                const isBanned = bannedUsers.includes(u.user_id);
                const canBan = isModerator && u.user_id !== user?.user_id && 
                  !['super_admin', 'admin', 'moderator'].includes(u.role);
                
                return (
                  <div key={u.user_id} className="relative">
                    <div 
                      onClick={() => canBan && setSelectedUser(selectedUser === u.user_id ? null : u.user_id)}
                      className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                        canBan ? 'cursor-pointer hover:bg-[#2a3142]' : ''
                      } ${isBanned ? 'opacity-50' : ''} ${selectedUser === u.user_id ? 'bg-[#2a3142]' : ''}`}
                    >
                      <Circle className={`w-2 h-2 ${isBanned ? 'text-red-400 fill-red-400' : 'text-green-400 fill-green-400'}`} />
                      <span className="text-sm text-white truncate flex items-center gap-1">
                        {u.gamertag || u.name}
                        {u.role === 'super_admin' && <Medal className="w-3 h-3 text-yellow-400" />}
                        {isBanned && <Ban className="w-3 h-3 text-red-400" />}
                      </span>
                      {u.role && (
                        <span className={`text-[10px] ml-auto px-1.5 py-0.5 rounded ${
                          u.role === 'super_admin' ? 'bg-red-500/20 text-red-400' :
                          u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                          u.role === 'moderator' ? 'bg-blue-500/20 text-blue-400' :
                          u.role === 'captain' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {u.role === 'super_admin' ? 'SA' :
                           u.role === 'admin' ? 'A' :
                           u.role === 'moderator' ? 'M' :
                           u.role === 'captain' ? 'C' : 'P'}
                        </span>
                      )}
                    </div>
                    
                    {/* Ban/Unban Menu */}
                    {selectedUser === u.user_id && canBan && (
                      <div className="absolute left-0 right-0 mt-1 bg-[#252a3a] rounded-lg shadow-xl border border-[#3a4152] z-50 overflow-hidden">
                        <div className="p-2 border-b border-[#3a4152]">
                          <p className="text-xs text-slate-400 text-center">{u.gamertag || u.name}</p>
                        </div>
                        {isBanned ? (
                          <button
                            onClick={() => handleUnbanUser(u.user_id, u.gamertag || u.name)}
                            className="w-full flex items-center gap-2 p-3 text-green-400 hover:bg-green-500/10 transition-colors"
                          >
                            <UserCheck className="w-4 h-4" />
                            <span className="text-sm">Sbanna utente</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBanUser(u.user_id, u.gamertag || u.name)}
                            className="w-full flex items-center gap-2 p-3 text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Ban className="w-4 h-4" />
                            <span className="text-sm">Banna utente</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {onlineUsers.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">Nessun utente attivo</p>
              )}
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="bg-[#1a1f2e] rounded-xl p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-[#00F0FF]" />
              <div>
                <h1 className="text-xl font-bold text-white">Chat Globale</h1>
                <p className="text-sm text-slate-400">{messages.length} messaggi</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Mobile online users toggle */}
              <button
                onClick={() => setShowMobileUsers(!showMobileUsers)}
                className="md:hidden flex items-center gap-1.5 text-green-400 text-sm bg-green-500/10 border border-green-500/30 px-2.5 py-1.5 rounded-lg"
                data-testid="mobile-users-toggle"
              >
                <Users className="w-4 h-4" />
                <span className="font-bold">{onlineUsers.length}</span>
              </button>
              {isSuperAdmin && (
                <Button
                  onClick={handleClearChat}
                  disabled={clearing}
                  variant="destructive"
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="clear-chat-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {clearing ? '...' : 'Svuota'}
                </Button>
              )}
              <div className={`text-xs flex items-center gap-1 ${connected ? 'text-green-400' : 'text-red-400'}`}>
                <Circle className={`w-2 h-2 ${connected ? 'fill-green-400' : 'fill-red-400'}`} />
                {connected ? 'Live' : 'Offline'}
              </div>
            </div>
          </div>

          {/* Mobile Online Users Panel */}
          {showMobileUsers && (
            <div className="md:hidden bg-[#1a1f2e] rounded-xl p-4 mb-4" data-testid="mobile-users-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-400" />
                  <h2 className="font-semibold text-white text-sm">Utenti Online</h2>
                  <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">
                    {onlineUsers.length}
                  </span>
                </div>
                <button onClick={() => setShowMobileUsers(false)} className="text-slate-400 hover:text-white">
                  <X size={18} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto">
                {onlineUsers.map((u) => {
                  const isBanned = bannedUsers.includes(u.user_id);
                  return (
                    <div
                      key={u.user_id}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#2a3142] text-sm ${isBanned ? 'opacity-50' : ''}`}
                    >
                      <Circle className={`w-2 h-2 ${isBanned ? 'text-red-400 fill-red-400' : 'text-green-400 fill-green-400'}`} />
                      <span className="text-white truncate max-w-[120px]">
                        {u.gamertag || u.name}
                      </span>
                      {u.role === 'super_admin' && <Medal className="w-3 h-3 text-yellow-400" />}
                      {isBanned && <Ban className="w-3 h-3 text-red-400" />}
                      <span className={`text-[10px] px-1 py-0.5 rounded ${
                        u.role === 'super_admin' ? 'bg-red-500/20 text-red-400' :
                        u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                        u.role === 'moderator' ? 'bg-blue-500/20 text-blue-400' :
                        u.role === 'captain' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {u.role === 'super_admin' ? 'SA' :
                         u.role === 'admin' ? 'A' :
                         u.role === 'moderator' ? 'M' :
                         u.role === 'captain' ? 'C' : 'P'}
                      </span>
                    </div>
                  );
                })}
                {onlineUsers.length === 0 && (
                  <p className="text-sm text-slate-500 text-center w-full py-2">Nessun utente attivo</p>
                )}
              </div>
            </div>
          )}
      
      {/* Messages */}
      <div 
        className="bg-[#1a1f2e] rounded-xl p-4 mb-4 overflow-y-auto"
        style={{ height: '400px' }}
      >
        {messages.length === 0 ? (
          <p className="text-slate-500 text-center py-8">Nessun messaggio</p>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => {
              const isOwn = msg.sender_id === user?.user_id;
              const roleInfo = getRoleInfo(msg.sender_role);
              
              return (
                <div 
                  key={msg.message_id} 
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[80%] p-3 rounded-xl ${
                      isOwn ? 'bg-[#00F0FF]' : 'bg-[#2a3142]'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`font-semibold text-sm ${isOwn ? 'text-white' : 'text-[#4FC3F7]'} flex items-center gap-1`}>
                        {msg.sender_gamertag || msg.sender_name || 'Utente'}
                        {msg.sender_role === 'super_admin' && (
                          <Medal className="w-4 h-4 text-yellow-400" />
                        )}
                      </span>
                      <span className={`text-xs ${isOwn ? 'text-white/60' : 'text-slate-500'}`}>
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                    <div className="mb-1">
                      <span className={`text-xs ${isOwn ? 'text-white/70' : roleInfo.color}`}>
                        {roleInfo.label}
                      </span>
                    </div>
                    <p className="text-white text-base leading-relaxed" style={{ fontSize: '16px' }}>{renderContent(msg.content)}</p>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Typing indicator */}
      {Object.keys(typingUsers).length > 0 && (
        <div className="text-xs text-[#00F0FF] mb-2 flex items-center gap-2 px-1" data-testid="global-typing-indicator">
          <span className="flex gap-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '120ms' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: '240ms' }} />
          </span>
          <span>
            {Object.values(typingUsers).slice(0, 3).join(', ')}
            {Object.keys(typingUsers).length > 3 && ` +${Object.keys(typingUsers).length - 3}`}
            {' '}
            {Object.keys(typingUsers).length > 1 ? 'stanno scrivendo...' : 'sta scrivendo...'}
          </span>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="flex gap-3 relative">
        <div className="relative" ref={emojiRef}>
          <Button
            type="button"
            onClick={() => setShowEmoji(!showEmoji)}
            className="bg-[#1a1f2e] hover:bg-[#2a3142] border border-[#2a3142]"
          >
            <Smile className="w-5 h-5 text-yellow-400" />
          </Button>
          
          {/* Emoji Picker */}
          {showEmoji && (
            <div className="absolute bottom-full left-0 mb-2 bg-[#1a1f2e] border border-[#2a3142] rounded-xl p-4 shadow-xl z-50">
              <div className="grid grid-cols-10 gap-2" style={{ width: '380px' }}>
                {EMOJI_LIST.map((emoji, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      addEmoji(emoji);
                      setShowEmoji(false);
                    }}
                    className="w-9 h-9 flex items-center justify-center hover:bg-[#2a3142] rounded-lg text-2xl transition-all hover:scale-110"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex-1 relative">
          {/* @Mention Autocomplete */}
          {showMentions && mentionResults.length > 0 && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#1a1f2e] border border-[#00F0FF]/20 rounded-xl shadow-xl z-50 max-h-48 overflow-y-auto" data-testid="mention-autocomplete">
              {mentionResults.map((u) => (
                <button
                  key={u.user_id}
                  type="button"
                  onClick={() => insertMention(u.gamertag || u.name)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#00F0FF]/10 text-left transition-colors"
                  data-testid={`mention-option-${u.user_id}`}
                >
                  <Circle className="w-2 h-2 text-green-400 fill-green-400 flex-shrink-0" />
                  <span className="text-[#00F0FF] font-semibold text-sm truncate">@{u.gamertag || u.name}</span>
                  {u.role && <span className="text-[10px] text-slate-500 uppercase ml-auto">{u.role}</span>}
                </button>
              ))}
            </div>
          )}
          <Input
            ref={inputRef}
            value={newMessage}
            onChange={handleInputChange}
            placeholder="Scrivi un messaggio... usa @ per taggare"
            className="w-full bg-[#1a1f2e] border-[#2a3142] text-white"
            maxLength={500}
            data-testid="chat-input"
          />
        </div>
        <Button 
          type="submit" 
          disabled={!newMessage.trim() || sending}
          className="bg-[#00F0FF] hover:bg-[#00F0FF]/90"
        >
          <Send className="w-5 h-5" />
        </Button>
      </form>
        </div>
      </div>
    </div>
  );
};

export default Chat;
